import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "../lib/api";
import { useAuth } from "./AuthContext";

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState(() => new Set());
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const reconnectTimer = useRef(null);
  const attempts = useRef(0);
  const shouldRun = useRef(false);
  const connectRef = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem("gm_token");
    if (!token || !shouldRun.current) return;

    const scheduleReconnect = () => {
      if (!shouldRun.current) return;
      clearTimeout(reconnectTimer.current);
      const delay = Math.min(1000 * 2 ** attempts.current, 15000);
      attempts.current += 1;
      reconnectTimer.current = setTimeout(() => connectRef.current && connectRef.current(), delay);
    };

    let ws;
    try {
      ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => { attempts.current = 0; };

    ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      if (data.type === "presence_snapshot") {
        setOnlineUsers(new Set(data.online || []));
      } else if (data.type === "presence") {
        setOnlineUsers((prev) => {
          const next = new Set(prev);
          if (data.online) next.add(data.user_id);
          else next.delete(data.user_id);
          return next;
        });
      }
      listeners.current.forEach((fn) => {
        try { fn(data); } catch {}
      });
    };

    ws.onclose = () => { scheduleReconnect(); };
    ws.onerror = () => { try { ws.close(); } catch {} };
  }, []);

  connectRef.current = connect;

  useEffect(() => {
    if (user && typeof user === "object") {
      shouldRun.current = true;
      attempts.current = 0;
      connect();
    }
    return () => {
      shouldRun.current = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
      setOnlineUsers(new Set());
    };
  }, [user, connect]);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  const isOnline = useCallback((id) => onlineUsers.has(id), [onlineUsers]);

  return (
    <SocketContext.Provider value={{ subscribe, send, isOnline, onlineUsers }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
